import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { Control, FieldPath, FieldValues } from 'react-hook-form'
import FormDropdownField from '../../../components/ui/form/FormDropdownField'
import { useSelectableRecordGroups } from '../hooks/useSelectableRecordGroups'
import { preselectedRecordGroupId, recordGroupOptions } from '../recordGroupOptions'
import type { CurrentRecordGroup } from '../types'
import '../recordGroups.css'

interface Props<T extends FieldValues> {
  control: Control<T>
  name: FieldPath<T>
  visible: boolean
  /** Düzenlenen kaydın grubu; `null` → yeni kayıt. */
  current: CurrentRecordGroup | null
  /** Kararlı olmalı (ör. `useCallback` ile `setValue`). */
  onPreselect: (id: number) => void
}

/**
 * Pop-up'ların en üstündeki zorunlu Grup alanı (122) — Toplantı Grupları ve Liste Yönetimi; seçenekler
 * kullanıcının grupları. Yeni kayıtta tek seçenek varsa seçili gelir. Bağımsız görev ve prensip kararında
 * alan yoktur: grubu sunucu üyelikten türetir (19.10.2026).
 */
export default function RecordGroupField<T extends FieldValues>({
  control,
  name,
  visible,
  current,
  onPreselect,
}: Readonly<Props<T>>) {
  const { t } = useTranslation()
  const groups = useSelectableRecordGroups(visible)
  const selectable = groups.data
  const isNew = current === null

  useEffect(() => {
    if (!visible || !isNew || !selectable) return
    const id = preselectedRecordGroupId(selectable)
    if (id !== null) onPreselect(id)
  }, [visible, isNew, selectable, onPreselect])

  const empty = groups.isSuccess && selectable?.length === 0

  return (
    <div className="rg-field">
      <FormDropdownField
        control={control}
        name={name}
        label={t('recordGroups.field')}
        options={recordGroupOptions(selectable ?? [], current)}
        placeholder={t('recordGroups.placeholder')}
        required
      />
      {empty && isNew ? <p className="rg-hint">{t('recordGroups.noGroup')}</p> : null}
    </div>
  )
}
